/**
 * PersonalboqenEditModal.jsx
 * Admin bearbeitet/befüllt einen Personalbogen vollständig – inkl. Admin-Unterschrift
 * Verwendet für:
 *  1. Direktes Ausfüllen vor Ort (Mitarbeiter ist anwesend)
 *  2. Nachbearbeitung nach Mitarbeiter-Einreichung
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from './ui/SignaturePad';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  CheckCircle2, Loader2, RefreshCw, User, CreditCard,
  Landmark, Briefcase, FileText, ChevronDown, ChevronUp,
  Building2, Shield,
} from 'lucide-react';import {
  IBANInput, AddressAutocomplete, KrankenkassenSelect, CountrySelect, SVNummerInput, KVTypSelect, GermanDateInput,
} from './PersonalboqenSmartFields';

const API = process.env.REACT_APP_BACKEND_URL;

function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-primary" />
          {title}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}

function YesNoField({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        {[['Ja', true], ['Nein', false]].map(([txt, val]) => (
          <button key={txt} type="button" onClick={() => onChange(val)}
            className={`flex-1 py-1.5 rounded-lg border text-xs font-medium transition-all ${value === val ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-muted-foreground/50'}`}>
            {txt}
          </button>
        ))}
      </div>
    </div>
  );
}

function DocCheck({ label, checked, onChange }) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${checked ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}
      onClick={() => onChange(!checked)}>
      <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${checked ? 'border-green-500 bg-green-500' : 'border-muted-foreground'}`}>
        {checked && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
      </div>
      {label}
    </div>
  );
}

export function PersonalboqenEditModal({ open, bogen, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasAdminSig, setHasAdminSig] = useState(false);
  const sigRef = useRef(null);
  const anSigRef = useRef(null);
  const rvSigRef = useRef(null);
  const rvAnSigRef = useRef(null);
  // RV-Arbeitgeberanteil state
  const [rvAg, setRvAg] = useState({
    betriebsnummer: '', eingangsdatum: '', wirksamkeitsdatum: '',
    ort_ag: '', datum_unterschrift_ag: new Date().toISOString().slice(0, 10),
  });
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!bogen) return;
    const ag = bogen.arbeitgeber || {};
    const pe = bogen.personal || {};
    const st = bogen.steuer || {};
    const ba = bogen.bank || {};
    const be = bogen.beschaeftigung || {};
    const do_ = bogen.dokumente || {};
    setForm({
      // Arbeitgeber
      firma: ag.firma || '', eintrittsdatum: ag.eintrittsdatum || '',
      berufsbezeichnung: ag.berufsbezeichnung || '', ausgeubte_taetigkeit: ag.ausgeubte_taetigkeit || '',
      kostenstelle: ag.kostenstelle || '', abt_nummer: ag.abt_nummer || '',
      // Vertragsdetails (Admin) – lese aus beschaeftigung ODER arbeitgeber (Fallback nach Wizard)
      arbeitszeit: be.arbeitszeit || ag.arbeitszeit || '',
      hauptbeschaeftigung: be.hauptbeschaeftigung ?? ag.hauptbeschaeftigung ?? true,
      // Vergütung
      vergutung: ag.vergutung || '',       // Neu: einfaches Vergütungs-Anzeigefeld aus Wizard
      lohnart: ag.lohnart || '',
      stundenlohn: ag.stundenlohn || '',
      stunden_pro_woche: ag.stunden_pro_woche || '',
      stunden_pro_monat: ag.stunden_pro_monat || '',
      monatslohn: ag.monatslohn || '',
      arbeitstage_woche: ag.arbeitstage_woche || '',
      urlaubstage: ag.urlaubstage || '',
      probezeit: be.probezeit ?? ag.probezeit ?? false,
      probezeit_dauer: be.probezeit_dauer || ag.probezeit_dauer || '',
      befristet: be.befristet ?? ag.befristet ?? false,   // Bugfix: war st.befristet (falsch)
      befristung_bis: be.befristung_bis || ag.befristung_bis || '',
      elterneigenschaft: ag.elterneigenschaft ?? be.elterneigenschaft ?? false,
      schulabschluss: be.schulabschluss || '', berufsausbildung: be.berufsausbildung || '',      // Personal (Mitarbeiter)
      familienname: pe.familienname || '', vorname: pe.vorname || '',
      geburtsname: pe.geburtsname || '',
      strasse: pe.strasse || '', hausnummer: pe.hausnummer || '',
      plz: pe.plz || '', ort: pe.ort || '',
      telefon: pe.telefon || '', email: pe.email || '',
      geschlecht: pe.geschlecht || '',
      geburtsdatum: pe.geburtsdatum || '', geburtsort: pe.geburtsort || '',
      geburtsland: pe.geburtsland || 'Deutschland',
      staatsangehoerigkeit: pe.staatsangehoerigkeit || 'Deutschland',
      sv_nummer: pe.sv_nummer || '', krankenkasse: pe.krankenkasse || '',
      sv_erstantrag: pe.sv_erstantrag || false,
      schwerbehindert: pe.schwerbehindert ?? false,
      schulabschluss: pe.schulabschluss || be.schulabschluss || '',
      berufsausbildung: pe.berufsausbildung || be.berufsausbildung || '',
      // Aufenthaltsstatus
      aufenthaltstitel_typ: pe.aufenthaltstitel_typ || '',
      aufenthalt_befristet: pe.aufenthalt_befristet ?? null,
      aufenthalt_bis: pe.aufenthalt_bis || '',
      nebenbeschaeftigung: ag.nebenbeschaeftigung ?? null,
      // Steuer
      steuer_id: st.steuer_id || '', steuerklasse: st.steuerklasse || '',
      konfession: st.konfession || '', kinderfreibetraege: st.kinderfreibetraege || '0',
      finanzamt_nr: st.finanzamt_nr || '',
      // Bank
      iban: ba.iban || '', bic: ba.bic || '', bank_name: ba.bank_name || '',
      // KV-Typ
      kv_typ: pe.kv_typ || '',
      // Stunden
      stunden_mo: be.stunden_mo || '', stunden_di: be.stunden_di || '',
      stunden_mi: be.stunden_mi || '', stunden_do: be.stunden_do || '',
      stunden_fr: be.stunden_fr || '', stunden_sa: be.stunden_sa || '',
      stunden_so: be.stunden_so || '',
      // Dokumente
      doc_arbeitsvertrag: do_.arbeitsvertrag ?? false,
      doc_sv_ausweis: do_.sv_ausweis ?? false,
      doc_kk: do_.kk_bescheinigung ?? false,
      doc_lohnsteuer: do_.lohnsteuer_bescheinigung ?? false,
      doc_vwl: do_.vwl_vertrag ?? false,
      doc_elterneigenschaft: do_.nachweis_elterneigenschaft ?? false,
    });
    setHasAdminSig(!!bogen.signature_arbeitgeber);
    // RV-Arbeitgeberanteil laden
    const rv_ag = bogen.rv_antrag_ag || {};
    setRvAg({
      betriebsnummer: rv_ag.betriebsnummer || ag.betriebsnummer || '',  // Fallback auf arbeitgeber.betriebsnummer (Auto-Fill)
      eingangsdatum: rv_ag.eingangsdatum || '',
      wirksamkeitsdatum: rv_ag.wirksamkeitsdatum || '',
      ort_ag: rv_ag.ort_ag || '',
      datum_unterschrift_ag: rv_ag.datum_unterschrift_ag || new Date().toISOString().slice(0, 10),
      an_merkblatt: (bogen.rv_antrag_an?.merkblatt_gelesen) || false,
      an_ort: bogen.rv_antrag_an?.ort_an || '',
      an_datum: bogen.rv_antrag_an?.datum_unterschrift_an || new Date().toISOString().slice(0, 10),
    });
  }, [bogen]);

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const fb = (field) => (val) => setForm(p => ({ ...p, [field]: val }));

  const handleSave = async () => {
    if (!bogen) return;
    setSaving(true);
    try {
      const sigDateEl = document.getElementById('admin-sig-date');
      const sigDateVal = sigDateEl?.value
        ? new Date(sigDateEl.value).toLocaleDateString('de-DE')
        : new Date().toLocaleDateString('de-DE');

      // Optionale AN-Unterschrift (vor Ort)
      const anSigDateEl = document.getElementById('an-sig-date');
      const anSigDateVal = anSigDateEl?.value
        ? new Date(anSigDateEl.value).toLocaleDateString('de-DE')
        : null;
      const anSig = (anSigRef.current && !anSigRef.current.isEmpty())
        ? anSigRef.current.toDataURL('image/png')
        : (bogen.signature_arbeitnehmer || null);

      const adminSig = (sigRef.current && !sigRef.current.isEmpty())
        ? sigRef.current.toDataURL('image/png')
        : (bogen.signature_arbeitgeber || null);

      await axios.put(`${API}/api/personalbogen/${bogen.id}/admin-complete`, {
        arbeitgeber: {
          firma: form.firma, eintrittsdatum: form.eintrittsdatum,
          berufsbezeichnung: form.berufsbezeichnung, ausgeubte_taetigkeit: form.ausgeubte_taetigkeit,
          kostenstelle: form.kostenstelle, abt_nummer: form.abt_nummer,
          vergutung: form.vergutung,           // Neu: einfaches Vergütungsfeld aus Wizard
          lohnart: form.lohnart, stundenlohn: form.stundenlohn,
          stunden_pro_woche: form.stunden_pro_woche, stunden_pro_monat: form.stunden_pro_monat,
          monatslohn: form.monatslohn, arbeitstage_woche: form.arbeitstage_woche,
          urlaubstage: form.urlaubstage,
          nebenbeschaeftigung: form.nebenbeschaeftigung,
          arbeitszeit: form.arbeitszeit,        // Bugfix: auch in arbeitgeber speichern
          hauptbeschaeftigung: form.hauptbeschaeftigung,
          probezeit: form.probezeit, probezeit_dauer: form.probezeit_dauer || null,
          befristet: form.befristet, befristung_bis: form.befristung_bis || null,
        },
        personal: {
          familienname: form.familienname, vorname: form.vorname,
          geburtsname: form.geburtsname, strasse: form.strasse,
          hausnummer: form.hausnummer, plz: form.plz, ort: form.ort,
          telefon: form.telefon, email: form.email,
          geschlecht: form.geschlecht, geburtsdatum: form.geburtsdatum,
          geburtsort: form.geburtsort,
          geburtsland: form.geburtsland,
          staatsangehoerigkeit: form.staatsangehoerigkeit,
          sv_nummer: form.sv_nummer, krankenkasse: form.krankenkasse,
          kv_typ: form.kv_typ,
          sv_erstantrag: form.sv_erstantrag,
          schwerbehindert: form.schwerbehindert,
          schulabschluss: form.schulabschluss,
          berufsausbildung: form.berufsausbildung,
          // Aufenthaltsstatus
          aufenthaltstitel_typ: form.aufenthaltstitel_typ || undefined,
          aufenthalt_befristet: form.aufenthalt_befristet !== null && form.aufenthalt_befristet !== undefined ? form.aufenthalt_befristet : undefined,
          aufenthalt_bis: form.aufenthalt_bis || undefined,
        },
        steuer: {
          steuer_id: form.steuer_id, steuerklasse: form.steuerklasse,
          konfession: form.konfession, kinderfreibetraege: form.kinderfreibetraege,
          finanzamt_nr: form.finanzamt_nr,
          elterneigenschaft: form.elterneigenschaft,
          befristet: form.befristet, befristung_bis: form.befristung_bis || null,
        },
        bank: { iban: form.iban, bic: form.bic, bank_name: form.bank_name },
        beschaeftigung: {
          arbeitszeit: form.arbeitszeit,
          hauptbeschaeftigung: form.hauptbeschaeftigung,
          probezeit: form.probezeit, probezeit_dauer: form.probezeit_dauer,
          schulabschluss: form.schulabschluss, berufsausbildung: form.berufsausbildung,
          stunden_mo: form.stunden_mo, stunden_di: form.stunden_di,
          stunden_mi: form.stunden_mi, stunden_do: form.stunden_do,
          stunden_fr: form.stunden_fr, stunden_sa: form.stunden_sa,
          stunden_so: form.stunden_so,
        },
        dokumente: {
          arbeitsvertrag: form.doc_arbeitsvertrag,
          sv_ausweis: form.doc_sv_ausweis,
          kk_bescheinigung: form.doc_kk,
          lohnsteuer_bescheinigung: form.doc_lohnsteuer,
          vwl_vertrag: form.doc_vwl,
          nachweis_elterneigenschaft: form.doc_elterneigenschaft,
        },
        signature_arbeitgeber: adminSig,
        datum_unterschrift_arbeitgeber: sigDateVal,
        // AN-Unterschrift wenn vor Ort
        ...(anSig ? { signature_arbeitnehmer: anSig } : {}),
        ...(anSigDateVal ? { datum_unterschrift_arbeitnehmer: anSigDateVal } : {}),
      }, { headers });

      toast.success('Personalbogen gespeichert!');

      // RV-Arbeitgeberanteil separat speichern wenn Minijob
      const isMinijob = (form.arbeitszeit === 'minijob') ||
                        (bogen.beschaeftigung?.arbeitszeit === 'minijob');
      if (isMinijob) {
        const rvSig = (rvSigRef.current && !rvSigRef.current.isEmpty())
          ? rvSigRef.current.toDataURL('image/png')
          : (bogen.rv_antrag_ag?.signature_ag || null);
        const rvDate = rvAg.datum_unterschrift_ag
          ? new Date(rvAg.datum_unterschrift_ag).toLocaleDateString('de-DE')
          : new Date().toLocaleDateString('de-DE');
        await axios.put(`${API}/api/personalbogen/${bogen.id}/admin-rv-complete`, {
          ...rvAg,
          datum_unterschrift_ag: rvDate,
          signature_ag: rvSig,
          an_merkblatt_gelesen: rvAg.an_merkblatt || false,
          an_ort: rvAg.an_ort || null,
          an_datum: rvAg.an_datum ? new Date(rvAg.an_datum).toLocaleDateString('de-DE') : null,
          an_signature: (rvAnSigRef.current && !rvAnSigRef.current.isEmpty())
            ? rvAnSigRef.current.toDataURL('image/png')
            : (bogen.rv_antrag_an?.signature_an || null),
        }, { headers });
      }

      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  if (!bogen) return null;

  const displayName = form.familienname
    ? `${form.vorname} ${form.familienname}`.trim()
    : bogen.arbeitgeber?.berufsbezeichnung || 'Neuer Mitarbeiter';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Personalbogen: {displayName}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Alle Felder bearbeiten · Admin-Sicht</p>
        </DialogHeader>

        {/* Scrollbarer Inhalt */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">

          {/* Arbeitgeber-Angaben */}
          <Section title="Arbeitgeber-Angaben" icon={Briefcase} defaultOpen>
            <Row>
              <Field label="Firma"><Input value={form.firma} onChange={f('firma')} className="h-8 text-sm" /></Field>
              <Field label="Eintrittsdatum"><Input type="date" value={form.eintrittsdatum} onChange={f('eintrittsdatum')} className="h-8 text-sm" /></Field>
            </Row>
            <Row>
              <Field label="Berufsbezeichnung"><Input value={form.berufsbezeichnung} onChange={f('berufsbezeichnung')} className="h-8 text-sm" /></Field>
              <Field label="Ausgeübte Tätigkeit"><Input value={form.ausgeubte_taetigkeit} onChange={f('ausgeubte_taetigkeit')} className="h-8 text-sm" /></Field>
            </Row>
            <Row>
              <Field label="Kostenstelle"><Input value={form.kostenstelle} onChange={f('kostenstelle')} className="h-8 text-sm" /></Field>
              <Field label="Abt.-Nummer"><Input value={form.abt_nummer} onChange={f('abt_nummer')} className="h-8 text-sm" /></Field>
            </Row>
            {/* Vertragsdetails */}
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Vergütung / Entlohnung</p>
              {/* Vergütung aus Wizard (einfaches Anzeigefeld) */}
              <Field label="Brutto-Vergütung (aus Personalbogen-Wizard)">
                <Input
                  value={form.vergutung || ''}
                  onChange={e => setForm(p => ({...p, vergutung: e.target.value}))}
                  placeholder="z.B. 14,50 €/Std. oder 800 €/Monat"
                  className="h-8 text-sm"
                  data-testid="edit-vergutung-input"
                />
              </Field>
              <Field label="Lohnart">
                <div className="flex gap-2">
                  {[['Stundenlohn','stundenlohn'],['Pauschal','pauschal'],['Monatslohn','monatslohn']].map(([l,v]) => (
                    <button key={v} type="button" onClick={() => setForm(p => ({...p, lohnart: v}))}
                      className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-all ${form.lohnart === v ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </Field>
              <Row>
                {(form.lohnart === 'stundenlohn') && (
                  <Field label="Stundenlohn (€)"><Input value={form.stundenlohn || ''} onChange={e => setForm(p => ({...p, stundenlohn: e.target.value}))} placeholder="12.50" className="h-8 text-sm" /></Field>
                )}
                {(form.lohnart === 'pauschal' || form.lohnart === 'monatslohn') && (
                  <Field label="Betrag/Monat (€)"><Input value={form.monatslohn || ''} onChange={e => setForm(p => ({...p, monatslohn: e.target.value}))} placeholder="520.00" className="h-8 text-sm" /></Field>
                )}
                <Field label="Std./Woche"><Input value={form.stunden_pro_woche || ''} onChange={e => setForm(p => ({...p, stunden_pro_woche: e.target.value}))} placeholder="z.B. 20" className="h-8 text-sm" /></Field>
              </Row>
              <Field label="Stunden/Monat"><Input value={form.stunden_pro_monat || ''} onChange={e => setForm(p => ({...p, stunden_pro_monat: e.target.value}))} placeholder="z.B. 80" className="h-8 text-sm" /></Field>
              <Row>
                <Field label="Arbeitstage/Woche">
                  <div className="space-y-1">
                    <Input
                      type="number" min="1" max="7" step="1"
                      value={form.arbeitstage_woche || ''}
                      onChange={e => {
                        const days = e.target.value;
                        const autoUrlaub = days ? String(Math.max(1, parseInt(days, 10) * 4)) : '';
                        setForm(p => ({ ...p, arbeitstage_woche: days, urlaubstage: autoUrlaub }));
                      }}
                      placeholder="z.B. 5"
                      className="h-8 text-sm w-24"
                      data-testid="edit-arbeitstage-input"
                    />
                    {form.arbeitstage_woche && (
                      <p className="text-xs text-muted-foreground">
                        {form.arbeitstage_woche} × 4 = <span className="font-medium text-foreground">{parseInt(form.arbeitstage_woche, 10) * 4} Tage</span> Mindestanspruch
                      </p>
                    )}
                  </div>
                </Field>
                <Field label="Urlaubstage/Jahr">
                  <div className="space-y-1">
                    <Input
                      type="number" min="1" max="365"
                      value={form.urlaubstage || ''}
                      onChange={e => setForm(p => ({...p, urlaubstage: e.target.value}))}
                      placeholder="z.B. 4"
                      className="h-8 text-sm w-24"
                      data-testid="edit-urlaubstage-input"
                    />
                    <p className="text-xs text-muted-foreground">Manuell überschreibbar</p>
                  </div>
                </Field>
              </Row>
            </div>
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Vertragsdetails</p>
              <Field label="Arbeitszeit">
                <div className="flex gap-2">
                  {[['Vollzeit','vollzeit'],['Teilzeit','teilzeit'],['Aushilfe (Minijob)','minijob'],['Werkstudent','werkstudent']].map(([l,v]) => (
                    <button key={v} type="button" onClick={() => setForm(p => ({...p, arbeitszeit: v}))}
                      className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-all ${form.arbeitszeit === v ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </Field>
              <Row>
                <YesNoField label="Hauptbeschäftigung?" value={form.hauptbeschaeftigung}
                  onChange={v => setForm(p => ({...p, hauptbeschaeftigung: v, nebenbeschaeftigung: v ? null : p.nebenbeschaeftigung}))} />
                <YesNoField label="Elterneigenschaft?" value={form.elterneigenschaft} onChange={fb('elterneigenschaft')} />
              </Row>
              {form.hauptbeschaeftigung === false && (
                <YesNoField label="Nebenbeschäftigung?" value={form.nebenbeschaeftigung} onChange={v => setForm(p => ({...p, nebenbeschaeftigung: v}))} />
              )}
              <Row>
                <YesNoField label="Probezeit?" value={form.probezeit} onChange={fb('probezeit')} />
                <YesNoField label="Befristet?" value={form.befristet} onChange={fb('befristet')} />
              </Row>
              {form.probezeit && <Field label="Probezeit-Dauer"><Input value={form.probezeit_dauer} onChange={f('probezeit_dauer')} placeholder="z.B. 3 Monate" className="h-8 text-sm" /></Field>}
              {form.befristet && <Field label="Befristet bis"><Input type="date" value={form.befristung_bis} onChange={f('befristung_bis')} className="h-8 text-sm" /></Field>}
            </div>
            {/* Stunden (Teilzeit) */}
            {form.arbeitszeit === 'teilzeit' && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Stundenverteilung</p>
                <div className="grid grid-cols-7 gap-1">
                  {[['Mo','stunden_mo'],['Di','stunden_di'],['Mi','stunden_mi'],['Do','stunden_do'],['Fr','stunden_fr'],['Sa','stunden_sa'],['So','stunden_so']].map(([d,k]) => (
                    <div key={k} className="space-y-0.5 text-center">
                      <Label className="text-xs">{d}</Label>
                      <Input type="number" min="0" max="12" step="0.5" value={form[k]} onChange={f(k)} className="h-7 text-xs text-center px-0.5" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* Persönliche Angaben */}
          <Section title="Persönliche Angaben (Mitarbeiter)" icon={User} defaultOpen={!form.familienname}>
            <Row>
              <Field label="Familienname *"><Input value={form.familienname} onChange={f('familienname')} className="h-8 text-sm" data-testid="admin-familienname" /></Field>
              <Field label="Vorname *"><Input value={form.vorname} onChange={f('vorname')} className="h-8 text-sm" /></Field>
            </Row>
            <Row>
              <Field label="Geburtsname">
                <Input
                  value={form.geburtsname}
                  onChange={f('geburtsname')}
                  className={`h-8 text-sm ${form.geburtsname && /^\d{2}\.\d{2}\.\d{4}$/.test(form.geburtsname.trim()) ? 'border-orange-400' : ''}`}
                />
                {form.geburtsname && /^\d{2}\.\d{2}\.\d{4}$/.test(form.geburtsname.trim()) && (
                  <p className="text-[10px] text-orange-600 mt-0.5">
                    Sieht aus wie ein Datum — bitte prüfen (Geburtsname ist der Nachname bei Geburt, z.B. Mayer)
                  </p>
                )}
              </Field>
              <Field label="Geschlecht">
                <select value={form.geschlecht} onChange={f('geschlecht')} className="h-8 w-full rounded-md border bg-background px-2 text-xs">
                  <option value="">–</option>
                  {['männlich','weiblich','diverse'].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
            </Row>
            {/* Adress-Autocomplete */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Adresse (Autocomplete)</Label>
              <AddressAutocomplete
                strasse={form.strasse}
                hausnummer={form.hausnummer}
                plz={form.plz}
                ort={form.ort}
                onChange={(updates) => setForm(prev => ({ ...prev, ...updates }))}
              />
            </div>
            <Row>
              <Field label="Staatsangehörigkeit">
                <CountrySelect
                  value={form.staatsangehoerigkeit}
                  onChange={val => setForm(p => ({...p, staatsangehoerigkeit: val}))}
                  testId="edit-staatsangehoerigkeit"
                  placeholder="Staatsangehörigkeit…"
                />
              </Field>
              <Field label="Geburtsort"><Input value={form.geburtsort} onChange={f('geburtsort')} className="h-8 text-sm" /></Field>
            </Row>
            <Field label="Geburtsland">
              <CountrySelect
                value={form.geburtsland}
                onChange={val => setForm(p => ({...p, geburtsland: val}))}
                testId="edit-geburtsland"
                placeholder="Geburtsland auswählen…"
              />
            </Field>
            <Row>
              <Field label="Telefon"><Input value={form.telefon} onChange={f('telefon')} className="h-8 text-sm" /></Field>
              <Field label="E-Mail"><Input value={form.email} onChange={f('email')} className="h-8 text-sm" /></Field>
            </Row>
            <Field label="Geburtsdatum"><GermanDateInput value={form.geburtsdatum} onChange={(iso) => setForm(p => ({...p, geburtsdatum: iso}))} className="h-8 text-sm" data-testid="edit-geburtsdatum" /></Field>
            <Field label="SV-Nummer">
              <Input value={form.sv_nummer} onChange={f('sv_nummer')} className="h-8 text-sm font-mono" />
            </Field>
            {/* SV-Erstantrag Checkbox */}
            <div
              className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${form.sv_erstantrag ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-border'}`}
              onClick={() => setForm(p => ({...p, sv_erstantrag: !p.sv_erstantrag}))}
              data-testid="edit-sv-erstantrag"
            >
              <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${form.sv_erstantrag ? 'border-amber-500 bg-amber-500' : 'border-muted-foreground'}`}>
                {form.sv_erstantrag && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
              </div>
              <span className="text-xs">SV-Nummer noch nicht bekannt – Erstantrag notwendig</span>
            </div>
            {/* Krankenkassen-Select */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Krankenkasse</Label>
              <KrankenkassenSelect
                value={form.krankenkasse}
                onChange={(val) => setForm(prev => ({ ...prev, krankenkasse: val }))}
              />
            </div>
            {/* KV-Typ */}
            <KVTypSelect
              value={form.kv_typ}
              onChange={val => setForm(p => ({...p, kv_typ: val}))}
              labelSize="text-xs text-muted-foreground"
            />
            <YesNoField label="Schwerbehindert?" value={form.schwerbehindert} onChange={fb('schwerbehindert')} />
            {/* Aufenthaltsstatus für Nicht-Deutsche */}
            {form.staatsangehoerigkeit && form.staatsangehoerigkeit !== 'Deutschland' && (
              <div className="border border-blue-200 dark:border-blue-800 rounded-xl p-3 bg-blue-50/40 dark:bg-blue-900/10 space-y-3">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Aufenthaltsstatus in Deutschland</p>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Art des Aufenthalts</Label>
                  <select
                    value={form.aufenthaltstitel_typ || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({
                        ...p,
                        aufenthaltstitel_typ: val,
                        aufenthalt_befristet: val === 'niederlassungserlaubnis' || val === 'eu_freizeit' ? false : p.aufenthalt_befristet,
                        aufenthalt_bis: val === 'niederlassungserlaubnis' || val === 'eu_freizeit' ? '' : p.aufenthalt_bis,
                      }));
                    }}
                    className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                    data-testid="edit-aufenthaltstitel-typ"
                  >
                    <option value="">Nicht angegeben</option>
                    <option value="eu_freizeit">EU-Bürger / Freizügigkeit</option>
                    <option value="niederlassungserlaubnis">Niederlassungserlaubnis (unbefristet)</option>
                    <option value="aufenthaltstitel">Aufenthaltstitel</option>
                    <option value="blaue_karte_eu">Blaue Karte EU</option>
                    <option value="fiktionsbescheinigung">Fiktionsbescheinigung</option>
                    <option value="sonstige">Sonstige</option>
                  </select>
                </div>
                {form.aufenthaltstitel_typ && !['eu_freizeit', 'niederlassungserlaubnis'].includes(form.aufenthaltstitel_typ) && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Gültigkeit</Label>
                      <div className="flex gap-2">
                        <button type="button"
                          onClick={() => setForm(p => ({ ...p, aufenthalt_befristet: false, aufenthalt_bis: '' }))}
                          className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-all ${form.aufenthalt_befristet === false ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700' : 'border-border text-muted-foreground'}`}>
                          Unbefristet
                        </button>
                        <button type="button"
                          onClick={() => setForm(p => ({ ...p, aufenthalt_befristet: true }))}
                          className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-all ${form.aufenthalt_befristet === true ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700' : 'border-border text-muted-foreground'}`}>
                          Befristet
                        </button>
                      </div>
                    </div>
                    {form.aufenthalt_befristet === true && (
                      <Field label="Gültig bis">
                        <Input type="date" value={form.aufenthalt_bis || ''} onChange={f('aufenthalt_bis')} className="h-8 text-sm" data-testid="edit-aufenthalt-bis" />
                      </Field>
                    )}
                    {form.aufenthalt_befristet === true && form.aufenthalt_bis && (() => {
                      const days = Math.ceil((new Date(form.aufenthalt_bis) - new Date()) / 86400000);
                      if (days <= 90) return (
                        <p className={`text-xs font-medium ${days <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {days <= 0 ? 'Abgelaufen!' : `Läuft in ${days} Tagen ab!`}
                        </p>
                      );
                      return null;
                    })()}
                  </>
                )}
              </div>
            )}
            <Row>
              <Field label="Höchster Schulabschluss">
                <select value={form.schulabschluss} onChange={f('schulabschluss')}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                  data-testid="edit-schulabschluss">
                  <option value="">–</option>
                  <option value="ohne_abschluss">Ohne Schulabschluss</option>
                  <option value="hauptschule">Haupt-/Volksschulabschluss</option>
                  <option value="mittlere_reife">Mittlere Reife</option>
                  <option value="abitur">Abitur / Fachabitur</option>
                  <option value="in_ausbildung">Noch in Ausbildung</option>
                </select>
              </Field>
              <Field label="Höchste Berufsausbildung">
                <select value={form.berufsausbildung} onChange={f('berufsausbildung')}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                  data-testid="edit-berufsausbildung">
                  <option value="">–</option>
                  <option value="ohne_ausbildung">Ohne Ausbildung</option>
                  <option value="berufsausbildung">Berufsausbildung</option>
                  <option value="meister">Meister / Techniker</option>
                  <option value="bachelor">Bachelor / Master / Diplom</option>
                  <option value="promotion">Promotion</option>
                  <option value="in_ausbildung">Noch in Ausbildung</option>
                </select>
              </Field>
            </Row>
          </Section>

          {/* Steuer */}
          <Section title="Steuer & Sozialversicherung" icon={CreditCard}>
            <Row>
              <Field label="Steuer-ID"><Input value={form.steuer_id} onChange={f('steuer_id')} className="h-8 text-sm" /></Field>
              <Field label="Steuerklasse">
                <select value={form.steuerklasse} onChange={f('steuerklasse')} className="h-8 w-full rounded-md border bg-background px-2 text-xs">
                  <option value="">–</option>
                  {['I','II','III','IV','V','VI'].map(k => <option key={k} value={k}>Klasse {k}</option>)}
                </select>
              </Field>
            </Row>
            <Row>
              <Field label="Konfession">
                {(() => {
                  const KNOWN = ['', 'ev', 'rk', 'lt', 'kein'];
                  const dropVal = KNOWN.includes(form.konfession) ? form.konfession : 'sonstige';
                  return (
                    <div className="space-y-1.5">
                      <select value={dropVal}
                        onChange={e => setForm(p => ({...p, konfession: e.target.value === 'sonstige' ? 'sonstige' : e.target.value}))}
                        className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                        data-testid="edit-konfession-select">
                        <option value="">–</option>
                        <option value="ev">Evangelisch (ev)</option>
                        <option value="rk">Römisch-Kath. (rk)</option>
                        <option value="lt">Lutherisch (lt)</option>
                        <option value="kein">Keine (kein)</option>
                        <option value="sonstige">Sonstige…</option>
                      </select>
                      {dropVal === 'sonstige' && (
                        <Input
                          value={form.konfession === 'sonstige' ? '' : form.konfession}
                          onChange={e => setForm(p => ({...p, konfession: e.target.value || 'sonstige'}))}
                          placeholder="z.B. fr, islamisch …"
                          className="h-7 text-xs"
                          data-testid="edit-konfession-input"
                        />
                      )}
                    </div>
                  );
                })()}
              </Field>
              <Field label="Kinderfreibeträge"><Input type="number" value={form.kinderfreibetraege} onChange={f('kinderfreibetraege')} min="0" step="0.5" className="h-8 text-sm" /></Field>
            </Row>
            <Field label="Finanzamt-Nummer"><Input value={form.finanzamt_nr} onChange={f('finanzamt_nr')} className="h-8 text-sm" /></Field>
          </Section>

          {/* Bank */}
          <Section title="Bankverbindung" icon={Landmark}>
            <IBANInput
              value={form.iban}
              onChange={(formatted, clean) => setForm(prev => ({ ...prev, iban: clean }))}
              onBICFound={(bic, bankName) => setForm(prev => ({ ...prev, bic, bank_name: bankName }))}
              labelSize="text-xs"
            />
            <Row>
              <Field label="BIC">
                <Input value={form.bic} onChange={f('bic')} className="h-8 text-sm font-mono" />
              </Field>
              <Field label="Kreditinstitut">
                <Input value={form.bank_name} onChange={f('bank_name')} className="h-8 text-sm" />
              </Field>
            </Row>
          </Section>

          {/* Arbeitspapiere */}
          <Section title="Arbeitspapiere (Admin-Checkliste)" icon={FileText}>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['doc_arbeitsvertrag','Arbeitsvertrag'],
                ['doc_sv_ausweis','SV-Ausweis'],
                ['doc_kk','KK-Mitgliedsbescheinigung'],
                ['doc_lohnsteuer','Lohnsteuerbescheinigung'],
                ['doc_vwl','VWL-Vertrag'],
                ['doc_elterneigenschaft','Nachweis Elterneigenschaft'],
              ].map(([field,label]) => (
                <DocCheck key={field} label={label} checked={!!form[field]}
                  onChange={v => setForm(p => ({...p, [field]: v}))} />
              ))}
            </div>
          </Section>

          {/* Werkstudent-Daten (nur bei Werkstudent) */}
          {(form.arbeitszeit === 'werkstudent' || bogen?.arbeitgeber?.arbeitszeit === 'werkstudent') && (
            <Section title="Werkstudent – Studium & Nachweis" icon={Shield} defaultOpen>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-2.5 text-xs text-indigo-700 dark:text-indigo-300 mb-2">
                Diese Daten werden vom Mitarbeiter im öffentlichen Formular ausgefüllt.
                Der Immatrikulationsnachweis muss jedes Semester aktualisiert werden.
              </div>
              <Row>
                <Field label="Hochschule / Universität">
                  <Input value={bogen?.personal?.hochschule || '–'} readOnly className="h-8 text-sm bg-muted/30" />
                </Field>
                <Field label="Matrikelnummer">
                  <Input value={bogen?.personal?.matrikelnummer || '–'} readOnly className="h-8 text-sm bg-muted/30 font-mono" />
                </Field>
              </Row>
              <Field label="Studiengang">
                <Input value={bogen?.personal?.studiengang || '–'} readOnly className="h-8 text-sm bg-muted/30" />
              </Field>
              {bogen?.personal?.immatrikulationsschein_url && (
                <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-700 rounded-xl p-3">
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-green-800 dark:text-green-300">Immatrikulationsnachweis hochgeladen</p>
                    {bogen?.personal?.immatrikulationsschein_datum && (
                      <p className="text-xs text-green-700 dark:text-green-400">Datum: {bogen.personal.immatrikulationsschein_datum}</p>
                    )}
                  </div>
                  <a href={bogen.personal.immatrikulationsschein_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors">
                    Ansehen
                  </a>
                </div>
              )}
              {!bogen?.personal?.immatrikulationsschein_url && (
                <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <span>⚠️</span> Noch kein Nachweis hochgeladen – Mitarbeiter bitte erinnern.
                </p>
              )}
            </Section>
          )}

          {/* RV-Arbeitgeberanteil (nur bei Minijob) */}
          {(form.arbeitszeit === 'minijob' || bogen?.beschaeftigung?.arbeitszeit === 'minijob') && (
            <Section title="RV-Befreiungsantrag – Arbeitgeberanteil" icon={Shield} defaultOpen>
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2.5 text-xs text-orange-700 dark:text-orange-300 mb-2">
                Nur ausfüllen, wenn der Mitarbeiter den RV-Antrag gestellt hat.
                Betriebsnummer wird für zukünftige Bögen gespeichert.
              </div>
              <Row>
                <Field label="Betriebsnummer">
                  <Input value={rvAg.betriebsnummer} onChange={e => setRvAg(r => ({...r, betriebsnummer: e.target.value}))} placeholder="z.B. 26111816" className="h-8 text-sm font-mono" />
                </Field>
                <Field label="Ort">
                  <Input value={rvAg.ort_ag} onChange={e => setRvAg(r => ({...r, ort_ag: e.target.value}))} placeholder="z.B. Fellbach" className="h-8 text-sm" />
                </Field>
              </Row>
              <Row>
                <Field label="Eingangsdatum des Antrags">
                  <Input type="date" value={rvAg.eingangsdatum} onChange={e => setRvAg(r => ({...r, eingangsdatum: e.target.value}))} className="h-8 text-sm" />
                </Field>
                <Field label="Wirksamkeit der Befreiung ab">
                  <Input type="date" value={rvAg.wirksamkeitsdatum} onChange={e => setRvAg(r => ({...r, wirksamkeitsdatum: e.target.value}))} className="h-8 text-sm" />
                </Field>
              </Row>
              <Field label="Unterschriftsdatum Arbeitgeber">
                <input type="date" value={rvAg.datum_unterschrift_ag} onChange={e => setRvAg(r => ({...r, datum_unterschrift_ag: e.target.value}))}
                  className="w-44 h-8 px-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </Field>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Unterschrift Arbeitgeber (RV-Antrag)</Label>
                <div className="border-2 border-orange-300 dark:border-orange-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                  <SignaturePad ref={rvSigRef} penColor="#EA580C" height={130} />
                </div>
                <button type="button" onClick={() => rvSigRef.current?.clear()}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                  <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
                </button>
              </div>

              {/* AN-Unterschrift für RV-Antrag (wenn Mitarbeiter vor Ort) */}
              <div className="border-t pt-3 mt-3 space-y-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Arbeitnehmer unterschreibt RV-Antrag
                  <span className="font-normal ml-1">(optional – nur wenn Mitarbeiter vor Ort)</span>
                </p>
                <div
                  className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${rvAg.an_merkblatt ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}
                  onClick={() => setRvAg(r => ({...r, an_merkblatt: !r.an_merkblatt}))}
                >
                  <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${rvAg.an_merkblatt ? 'border-green-500 bg-green-500' : 'border-muted-foreground'}`}>
                    {rvAg.an_merkblatt && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <span className="text-xs">AN hat das Merkblatt zur RV-Pflicht gelesen</span>
                </div>
                <Row>
                  <Field label="Ort (AN)"><Input value={rvAg.an_ort || ''} onChange={e => setRvAg(r => ({...r, an_ort: e.target.value}))} placeholder="z.B. Fellbach" className="h-8 text-sm" /></Field>
                  <Field label="Datum (AN)"><input type="date" value={rvAg.an_datum || new Date().toISOString().slice(0,10)} onChange={e => setRvAg(r => ({...r, an_datum: e.target.value}))} className="w-full h-8 px-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" /></Field>
                </Row>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Unterschrift Arbeitnehmer (RV-Antrag)</Label>
                  <div className="border-2 border-green-300 dark:border-green-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                    <SignaturePad ref={rvAnSigRef} penColor="#059669" height={130} />
                  </div>
                  <button type="button" onClick={() => rvAnSigRef.current?.clear()}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
                  </button>
                </div>
              </div>
            </Section>
          )}

          {/* Optionale AN-Unterschrift (bei Vor-Ort-Ausfüllung) */}
          <Section title="Unterschrift Arbeitnehmer (optional – nur wenn vor Ort)" icon={User}>
            <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2 mb-2">
              Nur ausfüllen wenn der Mitarbeiter gerade anwesend ist und direkt unterschreiben kann.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Unterschriftsdatum">
                <input type="date" id="an-sig-date"
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  className="w-full h-8 px-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </Field>
            </div>
            <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-900 mt-2">
              <SignaturePad ref={anSigRef} penColor="#1E40AF" height={180} />
            </div>
            <button type="button" onClick={() => anSigRef.current?.clear()}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mt-1">
              <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
            </button>
          </Section>

          {/* Admin-Unterschrift */}
          <Section title="Unterschrift Arbeitgeber (Admin)" icon={CheckCircle2} defaultOpen>
            {hasAdminSig && (
              <div className="text-xs text-green-600 dark:text-green-400 mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Unterschrift bereits vorhanden – neu zeichnen um zu ersetzen
              </div>
            )}
            <Field label="Unterschrifts-Datum">
              <input
                type="date"
                defaultValue={new Date().toISOString().slice(0, 10)}
                id="admin-sig-date"
                className="w-44 h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="admin-sig-date-input"
              />
            </Field>
            <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-900">
              <SignaturePad ref={sigRef} penColor="#1E40AF" height={180} />
            </div>
            <button
              type="button"
              onClick={() => { sigRef.current?.clear(); }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mt-1"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
            </button>
          </Section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-background flex gap-3 shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-admin-bogen-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Speichern & abschließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PersonalboqenEditModal;
